import type { AdminPermission } from "../../contracts/admin-permissions";
import type {
  AdminProjectionFreshness,
  AdminQueryMeta,
  AdminRecognitionAuditDetail,
  AdminRecognitionAuditPage,
  AdminRecognitionAuditQuery,
  AdminRecognitionAuditRow,
  AdminRecognitionRawTargetKind,
} from "../../contracts/admin-read-models";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";

type ViewModelOptions = {
  locale?: AdminDashboardLocale;
  permissions: readonly AdminPermission[];
};

type ListViewModelOptions = ViewModelOptions & {
  activeQuery: AdminRecognitionAuditQuery;
};

export type RecognitionAuditListViewModel = {
  requestId: string;
  generatedAtLabel: string;
  nextCursor?: string;
  sourceStatus: {
    kind: "live" | "snapshot" | "partial";
    generatedAtLabel: string;
  };
  summary: {
    totalRuns: RecognitionAuditSummaryMetric;
    failedOrCancelledRuns: RecognitionAuditSummaryMetric;
    finalSavedRuns: RecognitionAuditSummaryMetric;
    averageSourceFileCount: RecognitionAuditSummaryMetric;
    averageSourceImageCount: RecognitionAuditSummaryMetric;
    averageSourceBytes: RecognitionAuditSummaryMetric;
    averageRawResponseBytes: RecognitionAuditSummaryMetric;
    averageTotalTokens: RecognitionAuditSummaryMetric;
    averageEstimatedCostUsd: RecognitionAuditSummaryMetric;
  };
  columns: Array<{ id: string; label: string }>;
  rows: Array<{
    recognitionRunId: string;
    startedAtLabel: string;
    documentLabel: string;
    status: { value: AdminRecognitionAuditRow["status"]; label: string };
    outcome: { value: AdminRecognitionAuditRow["outcome"]; label: string };
    failedStage: { value?: string; label: string };
    errorCode: { value?: string; label: string };
    accountPetLabel: string;
    source: {
      summaryLabel: string;
      mimeTypeLabels: string[];
      remainingMimeTypeCountLabel?: string;
    };
    modelUsageLabel: string;
    reviewFinalLabel: string;
    rawAccess: {
      count: number;
      label: string;
      state: "hidden" | "disabled" | "available";
    };
  }>;
};

export type RecognitionAuditSummaryMetric = {
  value: number;
  label: string;
  contextLabel?: string;
};

export type RecognitionAuditDetailViewModel = {
  recognitionRunId: string;
  usageSummary: RecognitionAuditDetailUsageMetric[];
  sections: Array<{ id: string; title: string }>;
  relatedLinks: {
    aiJob?: { label: string; to?: string };
    draftRecord?: { label: string };
    finalRecord?: { label: string };
  };
  privacy: {
    consentStatusLabel: string;
    exportCandidateEligibleLabel: string;
    deletionStatusLabel: string;
    quarantineStatusLabel: string;
  };
  rawActions: {
    sourceFile: RecognitionAuditRawActionViewModel;
    sourceFiles: RecognitionAuditRawActionViewModel[];
    extractedText: RecognitionAuditRawActionViewModel;
    geminiResponse: RecognitionAuditRawActionViewModel;
  };
};

export type RecognitionAuditDetailUsageMetric = {
  id:
    | "files"
    | "images"
    | "sourceBytes"
    | "rawResponseBytes"
    | "totalTokens"
    | "averageModelTokens"
    | "totalCost"
    | "averageModelCost";
  label: string;
  valueLabel: string;
  contextLabel?: string;
};

export type RecognitionAuditRawActionViewModel = {
  targetKind: AdminRecognitionRawTargetKind;
  targetId?: string;
  state: "hidden" | "disabled" | "available";
};

function hasRawAccessPermission(permissions: readonly AdminPermission[]): boolean {
  return permissions.includes("admin.recognition_audits.raw_access");
}

function toFallbackLabel(value: string | undefined): string {
  return value && value.length > 0 ? value : "-";
}

function formatBytes(value: number): string {
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${formatAdminCount(value)} B`;
}

function formatTokens(value: number): string {
  return `${formatAdminCount(value)} tokens`;
}

function formatUsd(value: number): string {
  return `$${value.toFixed(4)}`;
}

function formatAverageCount(value: number, locale: AdminDashboardLocale, kind: "file" | "image"): string {
  const unit = locale === "ko" ? "개" : kind === "file" ? " files" : " images";
  return `${formatAdminCount(value)}${unit}`;
}

function formatRunCoverage(
  locale: AdminDashboardLocale,
  measuredRunCount: number,
  runCount: number,
): string | undefined {
  if (runCount === 0) {
    return undefined;
  }

  if (measuredRunCount < runCount) {
    return pickAdminDashboardLocaleText(locale, {
      en: `Measured ${formatAdminCount(measuredRunCount)}/${formatAdminCount(runCount)} runs`,
      ko: `측정 ${formatAdminCount(measuredRunCount)}/${formatAdminCount(runCount)}건`,
    });
  }

  return pickAdminDashboardLocaleText(locale, {
    en: `${formatAdminCount(runCount)} runs`,
    ko: `${formatAdminCount(runCount)}건 기준`,
  });
}

function toAverageMetric(
  value: number,
  runCount: number,
  measuredRunCount: number,
  locale: AdminDashboardLocale,
  formatValue: (value: number) => string,
): RecognitionAuditSummaryMetric {
  return {
    value,
    label: runCount === 0 ? "-" : formatValue(value),
    contextLabel: formatRunCoverage(locale, measuredRunCount, runCount),
  };
}

function formatMeasurementCoverage(
  locale: AdminDashboardLocale,
  measuredCount: number,
  totalCount: number,
  kind: "attempt" | "model",
): string | undefined {
  if (totalCount === 0 || measuredCount === totalCount) {
    return undefined;
  }

  return pickAdminDashboardLocaleText(locale, {
    en: `Measured ${formatAdminCount(measuredCount)}/${formatAdminCount(totalCount)} ${kind === "attempt" ? "attempts" : "models"}`,
    ko: kind === "attempt"
      ? `측정 ${formatAdminCount(measuredCount)}/${formatAdminCount(totalCount)}회`
      : `측정 ${formatAdminCount(measuredCount)}/${formatAdminCount(totalCount)}개 모델`,
  });
}

function toRecognitionAuditDetailUsageSummary(
  detail: AdminRecognitionAuditDetail,
  locale: AdminDashboardLocale,
): RecognitionAuditDetailUsageMetric[] {
  const sourceFileCount = detail.sources.length;
  const sourceImageCount = detail.sources.reduce((sum, source) => sum + source.imageCount, 0);
  const sourceBytes = detail.sources.reduce((sum, source) => sum + source.byteSize, 0);
  const rawResponseBytes = detail.aiUsage.reduce((sum, usage) => sum + usage.rawResponseBytes, 0);
  const measuredAttemptTokens = detail.aiUsage.filter((usage) => usage.totalTokens !== undefined);
  const measuredAttemptCosts = detail.aiUsage.filter((usage) => usage.estimatedCostUsd !== undefined);
  const totalTokens = measuredAttemptTokens.reduce((sum, usage) => sum + (usage.totalTokens ?? 0), 0);
  const totalCost = measuredAttemptCosts.reduce((sum, usage) => sum + (usage.estimatedCostUsd ?? 0), 0);
  const executedVariants = detail.modelComparison?.variants.filter(
    (variant) => variant.executionStatus !== "not_executed",
  ) ?? [];
  const measuredModelTokens = executedVariants.filter((variant) => variant.totalTokens !== undefined);
  const measuredModelCosts = executedVariants.filter((variant) => variant.estimatedCostUsd !== undefined);
  const averageModelTokens = measuredModelTokens.length > 0
    ? measuredModelTokens.reduce((sum, variant) => sum + (variant.totalTokens ?? 0), 0) / measuredModelTokens.length
    : undefined;
  const averageModelCost = measuredModelCosts.length > 0
    ? measuredModelCosts.reduce((sum, variant) => sum + (variant.estimatedCostUsd ?? 0), 0) / measuredModelCosts.length
    : undefined;

  return [
    {
      id: "files",
      label: pickAdminDashboardLocaleText(locale, { en: "Files", ko: "파일" }),
      valueLabel: formatAverageCount(sourceFileCount, locale, "file"),
    },
    {
      id: "images",
      label: pickAdminDashboardLocaleText(locale, { en: "Images", ko: "이미지" }),
      valueLabel: formatAverageCount(sourceImageCount, locale, "image"),
    },
    {
      id: "sourceBytes",
      label: pickAdminDashboardLocaleText(locale, { en: "Source size", ko: "원본 용량" }),
      valueLabel: formatBytes(sourceBytes),
    },
    {
      id: "rawResponseBytes",
      label: pickAdminDashboardLocaleText(locale, { en: "Raw JSON size", ko: "원문 JSON 용량" }),
      valueLabel: formatBytes(rawResponseBytes),
    },
    {
      id: "totalTokens",
      label: pickAdminDashboardLocaleText(locale, { en: "Total tokens", ko: "총 토큰" }),
      valueLabel: detail.aiUsage.length > 0 && measuredAttemptTokens.length === 0 ? "-" : formatTokens(totalTokens),
      contextLabel: formatMeasurementCoverage(locale, measuredAttemptTokens.length, detail.aiUsage.length, "attempt"),
    },
    {
      id: "averageModelTokens",
      label: pickAdminDashboardLocaleText(locale, { en: "Tokens per model", ko: "모델 평균 토큰" }),
      valueLabel: averageModelTokens === undefined ? "-" : formatTokens(averageModelTokens),
      contextLabel: formatMeasurementCoverage(locale, measuredModelTokens.length, executedVariants.length, "model"),
    },
    {
      id: "totalCost",
      label: pickAdminDashboardLocaleText(locale, { en: "Total estimated cost", ko: "총 예상 비용" }),
      valueLabel: detail.aiUsage.length > 0 && measuredAttemptCosts.length === 0 ? "-" : formatUsd(totalCost),
      contextLabel: formatMeasurementCoverage(locale, measuredAttemptCosts.length, detail.aiUsage.length, "attempt"),
    },
    {
      id: "averageModelCost",
      label: pickAdminDashboardLocaleText(locale, { en: "Cost per model", ko: "모델 평균 비용" }),
      valueLabel: averageModelCost === undefined ? "-" : formatUsd(averageModelCost),
      contextLabel: formatMeasurementCoverage(locale, measuredModelCosts.length, executedVariants.length, "model"),
    },
  ];
}

function formatDocumentKind(
  value: AdminRecognitionAuditRow["documentKind"],
  locale: AdminDashboardLocale,
): string {
  if (value === "health_record") {
    return pickAdminDashboardLocaleText(locale, { en: "Health record", ko: "건강 기록" });
  }

  return pickAdminDashboardLocaleText(locale, { en: "Receipt", ko: "영수증" });
}

function formatStatus(
  value: AdminRecognitionAuditRow["status"],
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "queued":
      return pickAdminDashboardLocaleText(locale, { en: "Queued", ko: "대기" });
    case "processing":
      return pickAdminDashboardLocaleText(locale, { en: "Processing", ko: "처리 중" });
    case "succeeded":
      return pickAdminDashboardLocaleText(locale, { en: "Succeeded", ko: "성공" });
    case "failed":
      return pickAdminDashboardLocaleText(locale, { en: "Failed", ko: "실패" });
    case "cancelled":
      return pickAdminDashboardLocaleText(locale, { en: "Cancelled", ko: "취소" });
  }
}

function formatOutcome(
  value: AdminRecognitionAuditRow["outcome"],
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "final_saved":
      return pickAdminDashboardLocaleText(locale, { en: "Final saved", ko: "최종 저장" });
    case "draft_only":
      return pickAdminDashboardLocaleText(locale, { en: "Draft only", ko: "초안만" });
    case "failed_before_ai":
      return pickAdminDashboardLocaleText(locale, { en: "Failed before AI", ko: "AI 이전 실패" });
    case "failed_during_ai":
      return pickAdminDashboardLocaleText(locale, { en: "Failed during AI", ko: "AI 중 실패" });
    case "failed_after_ai":
      return pickAdminDashboardLocaleText(locale, { en: "Failed after AI", ko: "AI 이후 실패" });
    case "user_discarded":
      return pickAdminDashboardLocaleText(locale, { en: "Discarded", ko: "사용자 폐기" });
  }
}

function deriveProjectionPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "live" | "snapshot" | "partial" {
  const values = Object.values(sourceFreshness);

  if (
    values.includes("stale") ||
    values.includes("partial") ||
    values.includes("unavailable")
  ) {
    return "partial";
  }

  return "live";
}

function deriveSourceStatus(
  meta: AdminQueryMeta,
  activeQuery: AdminRecognitionAuditQuery,
): RecognitionAuditListViewModel["sourceStatus"] {
  const projectionPriority = deriveProjectionPriority(meta.sourceFreshness);

  if (projectionPriority === "partial") {
    return {
      kind: "partial",
      generatedAtLabel: formatAdminDate(meta.generatedAt),
    };
  }

  if (
    activeQuery.sourceFreshness === "snapshot" ||
    activeQuery.cursor !== undefined ||
    meta.nextCursor !== undefined
  ) {
    return {
      kind: "snapshot",
      generatedAtLabel: formatAdminDate(meta.generatedAt),
    };
  }

  return {
    kind: "live",
    generatedAtLabel: formatAdminDate(meta.generatedAt),
  };
}

function deriveRawActionState(
  permissions: readonly AdminPermission[],
  enabled: boolean,
): "hidden" | "disabled" | "available" {
  if (!hasRawAccessPermission(permissions)) {
    return "hidden";
  }

  return enabled ? "available" : "disabled";
}

function hasPlausibleRowRawTarget(row: AdminRecognitionAuditRow): boolean {
  return (
    row.rawAccessCount > 0 ||
    row.aiProvider !== undefined ||
    row.aiModel !== undefined ||
    row.totalTokens !== undefined ||
    row.estimatedCostUsd !== undefined
  );
}

function toMimeTypeSummary(sourceMimeTypes: readonly string[]): {
  mimeTypeLabels: string[];
  remainingMimeTypeCountLabel?: string;
} {
  const labels = sourceMimeTypes.slice(0, 2);
  const remainingCount = sourceMimeTypes.length - labels.length;

  return {
    mimeTypeLabels: [...labels],
    remainingMimeTypeCountLabel: remainingCount > 0 ? `+${remainingCount}` : undefined,
  };
}

export function toRecognitionAuditListViewModel(
  page: AdminRecognitionAuditPage,
  meta: AdminQueryMeta,
  options: ListViewModelOptions,
): RecognitionAuditListViewModel {
  const locale = options.locale ?? "ko";
  const runCount = page.summary.runCount;

  return {
    requestId: meta.requestId,
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    nextCursor: page.nextCursor ?? meta.nextCursor,
    sourceStatus: deriveSourceStatus(meta, options.activeQuery),
    summary: {
      totalRuns: {
        value: runCount,
        label: formatAdminCount(runCount),
      },
      failedOrCancelledRuns: {
        value: page.summary.failedOrCancelledRunCount,
        label: formatAdminCount(page.summary.failedOrCancelledRunCount),
      },
      finalSavedRuns: {
        value: page.summary.finalSavedRunCount,
        label: formatAdminCount(page.summary.finalSavedRunCount),
      },
      averageSourceFileCount: toAverageMetric(
        page.summary.averageSourceFileCount,
        runCount,
        runCount,
        locale,
        (value) => formatAverageCount(value, locale, "file"),
      ),
      averageSourceImageCount: toAverageMetric(
        page.summary.averageSourceImageCount,
        runCount,
        runCount,
        locale,
        (value) => formatAverageCount(value, locale, "image"),
      ),
      averageSourceBytes: toAverageMetric(
        page.summary.averageSourceBytes,
        runCount,
        runCount,
        locale,
        formatBytes,
      ),
      averageRawResponseBytes: toAverageMetric(
        page.summary.averageRawResponseBytes.average,
        runCount,
        page.summary.averageRawResponseBytes.measuredRunCount,
        locale,
        formatBytes,
      ),
      averageTotalTokens: toAverageMetric(
        page.summary.averageTotalTokens.average,
        runCount,
        page.summary.averageTotalTokens.measuredRunCount,
        locale,
        formatTokens,
      ),
      averageEstimatedCostUsd: toAverageMetric(
        page.summary.averageEstimatedCostUsd.average,
        runCount,
        page.summary.averageEstimatedCostUsd.measuredRunCount,
        locale,
        formatUsd,
      ),
    },
    columns: [
      { id: "startedAt", label: pickAdminDashboardLocaleText(locale, { en: "Started", ko: "시작 시간" }) },
      { id: "document", label: pickAdminDashboardLocaleText(locale, { en: "Document", ko: "문서" }) },
      { id: "status", label: pickAdminDashboardLocaleText(locale, { en: "Status", ko: "상태" }) },
      { id: "outcome", label: pickAdminDashboardLocaleText(locale, { en: "Outcome", ko: "결과" }) },
      { id: "failedStage", label: pickAdminDashboardLocaleText(locale, { en: "Failure stage", ko: "실패 위치" }) },
      { id: "accountPet", label: pickAdminDashboardLocaleText(locale, { en: "Account / Pet", ko: "계정/반려동물" }) },
      { id: "source", label: pickAdminDashboardLocaleText(locale, { en: "Source", ko: "원본" }) },
      { id: "modelCost", label: pickAdminDashboardLocaleText(locale, { en: "Tokens / Cost", ko: "토큰/비용" }) },
      { id: "reviewFinal", label: pickAdminDashboardLocaleText(locale, { en: "Review / Final save", ko: "검토/최종저장" }) },
      { id: "rawAccess", label: pickAdminDashboardLocaleText(locale, { en: "Raw access", ko: "Raw 접근" }) },
    ],
    rows: page.items.map((row) => ({
      recognitionRunId: row.recognitionRunId,
      startedAtLabel: formatAdminDate(row.startedAt),
      documentLabel: formatDocumentKind(row.documentKind, locale),
      status: {
        value: row.status,
        label: formatStatus(row.status, locale),
      },
      outcome: {
        value: row.outcome,
        label: formatOutcome(row.outcome, locale),
      },
      failedStage: {
        value: row.failedStage,
        label: toFallbackLabel(row.failedStage),
      },
      errorCode: {
        value: row.errorCode,
        label: toFallbackLabel(row.errorCode),
      },
      accountPetLabel: row.petDisplayName
        ? `${row.accountDisplayName} / ${row.petDisplayName}`
        : row.accountDisplayName,
      source: {
        summaryLabel: pickAdminDashboardLocaleText(locale, {
          en: `Files ${formatAdminCount(row.sourceFileCount)} · Images ${formatAdminCount(row.sourceImageCount)} · Source ${formatBytes(row.totalSourceBytes)} · JSON ${formatBytes(row.rawResponseBytes)}`,
          ko: `파일 ${formatAdminCount(row.sourceFileCount)} · 이미지 ${formatAdminCount(row.sourceImageCount)} · 원본 ${formatBytes(row.totalSourceBytes)} · JSON ${formatBytes(row.rawResponseBytes)}`,
        }),
        ...toMimeTypeSummary(row.sourceMimeTypes),
      },
      modelUsageLabel: pickAdminDashboardLocaleText(locale, {
        en: `Total ${row.totalTokens === undefined ? "Not measured" : formatTokens(row.totalTokens)} · ${row.estimatedCostUsd === undefined ? "Not measured" : formatUsd(row.estimatedCostUsd)}`,
        ko: `총 ${row.totalTokens === undefined ? "미측정" : formatTokens(row.totalTokens)} · ${row.estimatedCostUsd === undefined ? "미측정" : formatUsd(row.estimatedCostUsd)}`,
      }),
      reviewFinalLabel: `${row.userReviewed ? pickAdminDashboardLocaleText(locale, { en: "Reviewed", ko: "검토 완료" }) : pickAdminDashboardLocaleText(locale, { en: "Not reviewed", ko: "미검토" })} / ${row.finalSaved ? pickAdminDashboardLocaleText(locale, { en: "Saved", ko: "저장됨" }) : pickAdminDashboardLocaleText(locale, { en: "Not saved", ko: "미저장" })}`,
      rawAccess: {
        count: row.rawAccessCount,
        label: formatAdminCount(row.rawAccessCount),
        state: deriveRawActionState(options.permissions, hasPlausibleRowRawTarget(row)),
      },
    })),
  };
}

export function toRecognitionAuditDetailViewModel(
  detail: AdminRecognitionAuditDetail,
  options: ViewModelOptions,
): RecognitionAuditDetailViewModel {
  const locale = options.locale ?? "ko";
  const sourceFileActions = detail.sources.map((source) => ({
    targetKind: "source_file" as const,
    targetId: source.sourceId,
    state: deriveRawActionState(options.permissions, source.sourceId.length > 0),
  }));
  const sourceFileAction = sourceFileActions[0] ?? {
    targetKind: "source_file" as const,
    targetId: undefined,
    state: deriveRawActionState(options.permissions, false),
  };

  return {
    recognitionRunId: detail.recognitionRunId,
    usageSummary: toRecognitionAuditDetailUsageSummary(detail, locale),
    sections: [
      { id: "lifecycle", title: pickAdminDashboardLocaleText(locale, { en: "Lifecycle", ko: "라이프사이클" }) },
      { id: "source_files", title: pickAdminDashboardLocaleText(locale, { en: "Source files", ko: "원본 파일" }) },
      { id: "extracted_text_extraction", title: pickAdminDashboardLocaleText(locale, { en: "Text extraction", ko: "텍스트 추출" }) },
      { id: "ai_usage_cost", title: pickAdminDashboardLocaleText(locale, { en: "AI usage / cost", ko: "AI 사용량 / 비용" }) },
      { id: "draft_final_diff", title: pickAdminDashboardLocaleText(locale, { en: "Draft / Final diff", ko: "초안 / 최종 diff" }) },
      { id: "related_links", title: pickAdminDashboardLocaleText(locale, { en: "Related links", ko: "관련 링크" }) },
      { id: "retention_consent", title: pickAdminDashboardLocaleText(locale, { en: "Retention / Consent", ko: "보관 / 동의" }) },
      { id: "raw_access_history", title: pickAdminDashboardLocaleText(locale, { en: "Raw access history", ko: "Raw 접근 이력" }) },
    ],
    relatedLinks: {
      aiJob: detail.relatedRecords.aiJobId
        ? {
            label: detail.relatedRecords.aiJobId,
            to:
              detail.relatedRecords.finalRecordId || detail.relatedRecords.draftRecordId
                ? `/admin/jobs?linkedRecordId=${encodeURIComponent(
                    detail.relatedRecords.finalRecordId ?? detail.relatedRecords.draftRecordId ?? "",
                  )}`
                : undefined,
          }
        : undefined,
      draftRecord: detail.relatedRecords.draftRecordId
        ? {
            label: detail.relatedRecords.draftRecordId,
          }
        : undefined,
      finalRecord: detail.relatedRecords.finalRecordId
        ? {
            label: detail.relatedRecords.finalRecordId,
          }
        : undefined,
    },
    privacy: {
      consentStatusLabel: detail.privacy.consentStatus,
      exportCandidateEligibleLabel: detail.privacy.exportCandidateEligible
        ? pickAdminDashboardLocaleText(locale, { en: "Yes", ko: "예" })
        : pickAdminDashboardLocaleText(locale, { en: "No", ko: "아니오" }),
      deletionStatusLabel: detail.privacy.deletionStatus,
      quarantineStatusLabel: detail.privacy.quarantineStatus,
    },
    rawActions: {
      sourceFile: sourceFileAction,
      sourceFiles: sourceFileActions,
      extractedText: {
        targetKind: "extracted_text",
        targetId: detail.textExtraction.rawTextTargetId,
        state: deriveRawActionState(options.permissions, detail.textExtraction.rawTextTargetId !== undefined),
      },
      geminiResponse: {
        targetKind: "gemini_response",
        targetId: detail.aiUsage.find((usage) => usage.rawResponseTargetId)?.rawResponseTargetId,
        state: deriveRawActionState(
          options.permissions,
          detail.aiUsage.some((usage) => usage.rawResponseTargetId !== undefined),
        ),
      },
    },
  };
}
